<template>
  <div class="product-option" :data-testid="option.code">
    <input
      type="radio"
      :name="option.code"
      :id="option.code"
      :value="option.code"
      :checked="selected"
      @change="optionSelected"
    />
    <label :for="option.code" class="product__information">
      <img
        class="product-option__img"
        v-if="option.image"
        :class="{ '--is-selected': selected }"
        :src="option.image"
        :alt="option.title"
      />
      <button
        type="button"
        class="product-option__info-btn"
        @click.stop="toggleInfoPanel"
      >
        ?
      </button>
      <div class="column-gap" />
      <h2 class="product-option__title">{{ option.title }}</h2>
      <div class="product-option__body" v-html="option.body" />
      <span class="product-option__price">
        <span v-if="isDefaultOption" class="product-option__price-current">
          Included in package
        </span>
        <span v-else>
          <span class="product-option__price-old" v-if="hasOldPrice">
            {{ formattedOldPriceDelta }}
          </span>
          <span class="product-option__price-current">
            {{ formattedPriceDelta }}
          </span>
          <small class="product-option__price-pppw">per person per week</small>
        </span>
      </span>

      <div class="check-mark" />

      <span
        class="product-option__ribbon"
        :data-testid="'ribbon_' + option.code"
        v-if="option.ribbon"
        :style="{ backgroundColor: option.ribbon.colour }"
        >{{ option.ribbon.text }}</span
      >
    </label>

    <ProductInfoOverlay
      v-if="infoPanelShowing"
      :title="option.title"
      :info="option.info"
      @close-info-panel="toggleInfoPanel"
    />
  </div>
</template>

<script>
import ProductInfoOverlay from "@/components/ProductInfoOverlay";

export default {
  name: "Product",
  props: {
    option: Object,
    defaultOption: Object,
    selected: Boolean,
  },
  data() {
    return {
      infoPanelShowing: false,
    };
  },
  computed: {
    hasOldPrice() {
      return !!this.option.price.old;
    },
    formattedOldPriceDelta() {
      const delta = this.calculatePriceDelta(this.option.price.old);
      return `+£${delta.toFixed(2)}`;
    },
    isDefaultOption() {
      return this.option === this.defaultOption;
    },
    formattedPriceDelta() {
      if (this.option.price.current === 0) {
        return "£" + this.option.price.current.toFixed(2);
      }

      const delta = this.calculatePriceDelta(this.option.price.current);
      return `${delta < 0 ? "-" : "+"}£${Math.abs(delta).toFixed(2)}`;
    },
  },
  methods: {
    calculatePriceDelta(startPrice) {
      // TODO: what if it is the default price?
      return (startPrice - this.defaultOption.price.current) / 100;
    },
    toggleInfoPanel() {
      this.infoPanelShowing = !this.infoPanelShowing;
    },
    optionSelected(event) {
      this.$emit("option-selected", event.target.value);
    },
  },
  components: {
    ProductInfoOverlay,
  },
};
</script>

<style lang="scss" scoped>
.product-option {
  max-width: 45rem;

  &:not(:last-of-type) {
    margin-bottom: $spacing-medium;
  }
}

input {
  @include visuallyhidden;
}

.product__information {
  display: grid;
  grid-template-columns: min-content min-content 1fr min-content;
  grid-template-rows: repeat(3, auto);

  position: relative;
  overflow: hidden;

  cursor: pointer;
  background-color: $white;
  border: 2px solid $grey-mid;
  border-radius: $border-radius-small;
  padding: $spacing-small;

  input:checked + &,
  input:focus + & {
    border: 2px solid $brand-blue;
  }

  input:not(:checked) + & {
    color: $grey-darkish;
  }

  .product-option__img {
    grid-row: 1 / 3;
    grid-column: 1 / 2;

    max-width: 40px;
    max-height: 45px;
    min-width: 30px;
    fill: currentColor;
    opacity: 0.7;
    filter: grayscale(100%);
    transition: opacity 0.2s linear, filter 0.2s linear;

    input:checked + & {
      filter: none;
      opacity: 1;
    }
  }

  .column-gap {
    grid-row: 1 / 4;
    grid-column: 2;

    width: 14px;
    min-width: 14px;
    max-width: 14px;
  }

  .product-option__title {
    grid-row: 1;
    grid-column: 3;

    font-size: $button-font-size;
    text-align: initial;
    font-weight: $font-medium;
    margin: 0;

    @include media("<medium") {
      padding-right: 2rem;
    }
  }

  .product-option__body {
    grid-row: 2;
    grid-column: 3;

    font-size: 1.6rem;
    margin-bottom: $spacing-tiny;

    * {
      margin-top: 0;
    }

    :first-child {
      margin-top: 0;
    }

    &:last-child,
    :last-child {
      margin-bottom: 0;
    }

    ::v-deep ul,
    ::v-deep ol {
      padding: 0 0 0 19px;
      margin: 0;
    }
  }

  .product-option__price {
    grid-row: 3;
    grid-column: 3;

    &-old {
      font-weight: bold;
      font-size: 14px;
      text-decoration: line-through;
    }

    &-current {
      font-weight: bold;
      font-size: 22px;

      input:checked + & {
        color: $brand-orange;
      }
    }

    &-pppw {
      font-weight: $font-regular;
      font-size: 14px;
    }
  }

  .product-option__info-btn {
    grid-row: 3;
    grid-column: 1;

    align-self: center;
    justify-self: center;

    text-align: center;
    width: 17px;
    height: 17px;
    font-size: 1.2rem;
    font-weight: 500;
    color: $white;
    background-color: #afafaf;
    border: none;
    border-radius: 50%;
    padding: 0;
    margin: 0;
  }

  .check-mark {
    grid-row: 3;
    grid-column: 4;

    align-self: end;

    min-width: 22px;
    min-height: 22px;
    max-width: 22px;
    max-height: 22px;

    background-color: $white;
    border: 2px solid $grey-darkish;
    border-radius: 50%;
    box-shadow: inset 0 0 0 3px $white;
    margin-left: 4px;

    input:checked + & {
      background-color: $brand-blue;
    }
  }

  .product-option__ribbon {
    position: absolute;
    top: 15px;
    right: -26px;
    width: 100px;
    text-align: center;
    text-transform: uppercase;
    font-size: 1.1rem;
    color: $white;
    background-color: $grey-dark;
    transform: rotate(45deg);
    padding: 3px 5px;
  }
}
</style>
